import { fireEvent, render, screen, waitFor } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { expect } from 'chai';
import { VNode } from 'preact';

import {
  CurrentItemVariant,
  ItemActionDetail,
  Metadata,
  SelectionMode
} from '../../../UNSAFE_Collection';
import { RootEnvironmentProvider } from '../../../UNSAFE_Environment';
import type { RootEnvironment } from '../../../UNSAFE_Environment';
import { Gridlines, ListViewScrollPosition } from '../../../UNSAFE_ListView';
import { Keys } from '../../../utils/UNSAFE_keys';
import { matchTranslationBundle } from '../../../utils/UNSAFE_matchTranslationBundle';
import { CollectionGestureContext } from '../../../hooks/PRIVATE_useCollectionGestureContext';
import { MenuItem } from '../../../UNSAFE_Menu';
import translations from '../../../resources/nls/en/bundle';

import { populateData } from './listViewTestFixtures';
import type { Employee } from './listViewTestFixtures';
import {
  getContents,
  getItems,
  getRoot,
  mouseItemSelection,
  fireDragAndDropEvent,
  keyboardReorder
} from './testHelpers';
import {
  getAllMenuItemsByRole,
  getMenuItemByRole,
  getLabel
} from '../../../UNSAFE_Menu/__tests__/menuTestsUtils';
import { xUnits } from '../../../UNSAFE_Theme/utils';
import { ListViewContextMenuConfig } from '../../../UNSAFE_ListView/List.types';
import { getClientHints } from '../../../utils/PRIVATE_clientHints';

//TODO: JET-63873
function getSparkle(grid: HTMLElement) {
  return grid.querySelector('.oj-collection-sparkle')?.firstElementChild as HTMLElement;
}
export type Props<K, D> = {
  currentItemVariant?: CurrentItemVariant;
  data: { data: Employee<K>; metadata: Metadata<K> }[];
  gridlines?: Gridlines;
  initialKey?: K;
  onItemAction?: (detail: ItemActionDetail<K, D>) => void;
  selectedKeys?: Keys<K>;
  selectionMode?: SelectionMode;
  onClick?: () => void;
  scrollPositionOverride?: ListViewScrollPosition<K>;
  isReorderEnabled?: boolean;
  contextMenuConfig?: ListViewContextMenuConfig<K, D>;
};

type TestComponentProps<K extends string | number, D> = (props: Props<K, D>) => VNode;

export const testRender = (TestComponent: TestComponentProps<any, any>) => {
  it('initial render', async function () {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems();
    expect(items.length).eq(25);
  });

  // skip this test in jest, only run it in karma mocha
  if (typeof jest == typeof undefined) {
    it('initial render with sparkle', async () => {
      const data = populateData(100) as Employee<string>[];
      let dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
        (data) => {
          return { data: data, metadata: { key: data.id } };
        }
      );

      // add suggestions
      dataMetadata[3].metadata.suggestion = { enable: true };
      dataMetadata[7].metadata.suggestion = { enable: true };
      dataMetadata[9].metadata.suggestion = { enable: true };

      const suggestions = dataMetadata.filter((d) => d.metadata.suggestion);
      dataMetadata = dataMetadata.filter((d) => !d.metadata.suggestion);
      dataMetadata.unshift(...suggestions);

      render(<TestComponent data={dataMetadata}></TestComponent>);
      const grid = await getRoot();
      const sparkle = getSparkle(grid);
      expect(sparkle !== undefined).eq(true);

      const items = await getItems();
      expect(items?.length).eq(25);
      expect(items[0].dataset['ojSuggestion']).eq('true');
      expect(items[1].dataset['ojSuggestion']).eq('true');
      expect(items[2].dataset['ojSuggestion']).eq('true');

      const sparkleHeight = items[0].offsetHeight + items[1].offsetHeight + items[2].offsetHeight;
      expect(sparkleHeight).eq(sparkle.offsetHeight);
    });
  }
};

export const testContextMenu = (TestComponent: TestComponentProps<string, Employee<any>>) => {
  it("Jet context menu appears when there's no text selection", async () => {
    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent
          data={dataMetadata}
          contextMenuConfig={{
            itemsRenderer: (context) => {
              const data = context.data;
              return (
                <>
                  <MenuItem label={`MenuItem${data ? data.id : ''}`}></MenuItem>
                  <MenuItem label="MenuItem2"></MenuItem>
                  <MenuItem label="MenuItem3"></MenuItem>
                </>
              );
            }
          }}></TestComponent>
      </RootEnvironmentProvider>
    );
    const items = await getContents();
    const grid = await getRoot();
    const firstItem = items[0];

    fireEvent.contextMenu(firstItem, {
      clientX: 50,
      clientY: 50,
      currentTarget: grid
    });

    const menu = getMenuItemByRole('menu');
    const menuItems = getAllMenuItemsByRole('menuitem');
    //With this we check context menu context was set correctly
    const menuItemGotByLabel = getLabel('MenuItemi0');

    expect(menu).not.null;
    expect(menuItems.length).equals(3);
    expect(menuItemGotByLabel).not.null;
  });

  it('ContextMenu on Shift-F10', async () => {
    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent
          data={dataMetadata}
          contextMenuConfig={{
            itemsRenderer: (context) => {
              const data = context.data;
              return (
                <>
                  <MenuItem label={`MenuItem${data ? data.id : ''}`}></MenuItem>
                  <MenuItem label="MenuItem2"></MenuItem>
                  <MenuItem label="MenuItem3"></MenuItem>
                </>
              );
            }
          }}></TestComponent>
      </RootEnvironmentProvider>
    );
    const grid = await getRoot();
    const items = await getContents();
    const firstItem = items[0];

    //We do the necessary to have firstItem as current item
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(firstItem.id);

    fireEvent.keyDown(grid, { key: 'F10', shiftKey: true });

    const menu = getMenuItemByRole('menu');
    const menuItems = getAllMenuItemsByRole('menuitem');
    //With this we check context menu context was set correctly
    const menuItemGotByLabel = getLabel('MenuItemi0');

    expect(menu).not.null;
    expect(menuItems.length).equals(3);
    expect(menuItemGotByLabel).not.null;
  });

  it("Jet context menu doesn't appear when there is text selection", async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    render(
      <RootEnvironmentProvider>
        <TestComponent
          data={dataMetadata}
          contextMenuConfig={{
            itemsRenderer: (context) => {
              const data = context.data;
              return (
                <>
                  <MenuItem label={`MenuItem${data ? data.id : ''}`}></MenuItem>
                  <MenuItem label="MenuItem2"></MenuItem>
                  <MenuItem label="MenuItem3"></MenuItem>
                </>
              );
            }
          }}></TestComponent>
      </RootEnvironmentProvider>
    );
    const items = await getContents();
    const grid = await getRoot();
    const firstItem = items[0];

    const elem = await screen.findByText(firstItem.textContent || '');

    // Set text selection
    await userEvent.pointer([
      { target: elem, offset: 0, keys: '[MouseLeft>]' },
      { offset: 5 },
      { keys: '[/MouseLeft]' }
    ]);

    fireEvent.contextMenu(firstItem, {
      clientX: 50,
      clientY: 50,
      currentTarget: grid
    });

    const menu = screen.queryByRole('menu');

    expect(menu).null;
  });

  it('Ios context menu appears after 1500ms', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    render(
      <RootEnvironmentProvider>
        <TestComponent
          data={dataMetadata}
          contextMenuConfig={{
            itemsRenderer: (context) => {
              const data = context.data;
              return (
                <>
                  <MenuItem label={`MenuItem${data ? data.id : ''}`}></MenuItem>
                  <MenuItem label="MenuItem2"></MenuItem>
                  <MenuItem label="MenuItem3"></MenuItem>
                </>
              );
            }
          }}></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents();
    const firstItem = items[0];

    if (typeof jest === 'undefined') {
      const { deviceType, platform } = getClientHints();
      const isMobile = deviceType === 'phone';
      const isIos = platform === 'ios';

      if (isMobile && isIos) {
        const timeout = (ms: number) => {
          return new Promise((resolve) => setTimeout(resolve, ms));
        };
        // Touch objects are created so we can replicate this in fake touch events
        // This stuff is based on what useSwipe triggers and could change depending on the final decision
        // on what events to trigger on useSwipe.
        const touch1 = new Touch({
          target: firstItem,
          identifier: 0,
          pageX: 386,
          pageY: 224
        });

        if (firstItem) {
          fireEvent.touchStart(firstItem, {
            timeStamp: 10406,
            currentTarget: firstItem,
            touches: [touch1]
          });
        }
        await timeout(1550);
        const menu = getMenuItemByRole('menu');
        const menuItems = getAllMenuItemsByRole('menuitem');
        //With this we check context menu context was set correctly
        const menuItemGotByLabel = getLabel('MenuItemi0');

        expect(menu).not.null;
        expect(menuItems.length).equals(3);
        expect(menuItemGotByLabel).not.null;
      }
    }
  });
};

export const testAria = (TestComponent: TestComponentProps<any, any>) => {
  it('aria structure and properties (select-none)', async function () {
    const data = populateData(10) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot();
    expect(grid.getAttribute('aria-rowcount')).eq('10');
    expect(grid.getAttribute('aria-multiselectable')).eq(null);

    const items = await getItems();
    expect(items.length).eq(10);
    expect(items[0].getAttribute('aria-rowindex')).eq('1');

    const cells = await getContents();
    expect(cells.length).eq(10);
    expect(cells[0].getAttribute('aria-colindex')).eq('1');
    expect(cells[0].getAttribute('aria-selected')).eq(null);
  });

  it('aria structure and properties (single-select)', async function () {
    const data = populateData(10) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata} selectionMode="single"></TestComponent>);
    const grid = await getRoot();
    expect(grid.getAttribute('aria-rowcount')).eq('10');
    expect(grid.getAttribute('aria-multiselectable')).eq('false');

    const items = await getItems();
    expect(items.length).eq(10);
    expect(items[0].getAttribute('aria-rowindex')).eq('1');

    const cells = await getContents();
    expect(cells.length).eq(10);
    expect(cells[0].getAttribute('aria-colindex')).eq('1');
    expect(cells[0].getAttribute('aria-selected')).eq('false');
  });

  it('aria structure and properties (multi-select)', async function () {
    const data = populateData(10) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const grid = await getRoot();
    expect(grid.getAttribute('aria-rowcount')).eq('10');
    expect(grid.getAttribute('aria-multiselectable')).eq('true');

    const items = await getItems();
    expect(items.length).eq(10);
    expect(items[0].getAttribute('aria-rowindex')).eq('1');

    const cells = await getContents();
    expect(cells.length).eq(10);
    expect(cells[0].getAttribute('aria-colindex')).eq('1');
    expect(cells[0].getAttribute('aria-selected')).eq('false');
  });
};

export const testKeys = (TestComponent: TestComponentProps<any, any>) => {
  it('supports currentKey logic with numeric key', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot();
    expect(grid.hasAttribute('aria-activedescendant')).eq(false);
    const items = await getContents();
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
  });

  it('supports currentKey logic with string key', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot();
    expect(grid.hasAttribute('aria-activedescendant')).eq(false);
    const items = await getContents();
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
  });

  it('checking key type - string key', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems();
    expect((items[0] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[1] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[2] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
  });

  it('checking key type - numeric key', async () => {
    const data = populateData(3, true) as Employee<number>[];
    const dataMetadata: { data: Employee<number>; metadata: Metadata<number> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems();
    expect((items[0] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[1] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[2] as HTMLElement).dataset['ojKeyType']).eq('number');
  });
};

export const testGridlines = (TestComponent: TestComponentProps<any, any>) => {
  const isTopGridlineVisible = (item: HTMLElement) => {
    const className = item.className;
    return className.indexOf('gridlineTop_visible') > 0;
  };

  const isBottomGridlineVisible = (item: HTMLElement) => {
    const className = item.className;
    return className.indexOf('gridlineBottom_visible') > 0;
  };

  const isGridlineHidden = (item: HTMLElement) => {
    return !isTopGridlineVisible(item) && !isBottomGridlineVisible(item);
  };

  it('gridlines - gridlines are hidden', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const items = await getItems();
    expect(isGridlineHidden(items[0])).to.be.true;
    expect(isGridlineHidden(items[1])).to.be.true;
    expect(isGridlineHidden(items[2])).to.be.true;
  });

  it('gridlines - bottom gridlines are visible', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visible = { item: 'visible' as const, bottom: 'visible' as const };
    render(<TestComponent data={dataMetadata} gridlines={visible}></TestComponent>);
    const items = await getItems();
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.true;
  });

  it('gridlines - bottom gridlines are visible except for the last list item', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visibleExceptLast = { item: 'visible' as const };
    render(<TestComponent data={dataMetadata} gridlines={visibleExceptLast}></TestComponent>);
    const items = await getItems();
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.false;
  });

  it('gridlines - bottom gridlines are visible; The top gridlines of the first list item is visible', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visibleWithFirst = {
      item: 'visible' as const,
      top: 'visible' as const,
      bottom: 'visible' as const
    };
    render(<TestComponent data={dataMetadata} gridlines={visibleWithFirst}></TestComponent>);
    const items = await getItems();
    expect(isTopGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.true;
  });

  it('gridlines - bottom gridlines are visible except for the last list item. The top gridlines of the first list item is visible.', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const visibleWithFirstButNotLast = { item: 'visible' as const, top: 'visible' as const };
    render(
      <TestComponent data={dataMetadata} gridlines={visibleWithFirstButNotLast}></TestComponent>
    );
    const items = await getItems();
    expect(isTopGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[0])).to.be.true;
    expect(isBottomGridlineVisible(items[1])).to.be.true;
    expect(isBottomGridlineVisible(items[2])).to.be.false;
  });
};

export const testItemAction = (TestComponent: TestComponentProps<any, any>) => {
  const isMetadataEqual = (metadata1: Metadata<any>, metadata2: Metadata<any>) => {
    return metadata1.key === metadata2.key;
  };

  const isFocusHighlight = (item: HTMLElement) => {
    const className = item.className;
    return (
      className.indexOf('focusHighlight_isFocusHighlight') > 0 ||
      className.indexOf('MultiVariantStyles_compound') > 0
    );
  };

  it('ItemAction - test click on item', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 0) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[2].metadata),
          'Context metadata is correct'
        ).eq(true);
      }
      count++;
    };
    render(<TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>);
    const items = await getItems('row');
    fireEvent.click(items[1]);
    fireEvent.click(items[1]);
    fireEvent.click(items[2]);
    expect(count).eq(3);
  });

  it('ItemAction - test press enter on item', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 3) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[2].metadata),
          'Context metadata is correct'
        ).eq(true);
      }
      count++;
    };
    render(<TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>);
    const grid = await getRoot();
    const items = await getItems('row');
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[1], { key: 'Enter' });
    fireEvent.keyDown(items[1], { key: 'Enter' });
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[2], { key: 'Enter' });
    expect(count).eq(4);
  });

  it('ItemAction - test press spacebar on item (Context value is embedded)', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[1].metadata),
          'Context metadata is correct'
        ).eq(true);
      } else if (count === 3) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(
          isMetadataEqual(detail.context.metadata, dataMetadata[2].metadata),
          'Context metadata is correct'
        ).eq(true);
      }
      count++;
    };
    render(
      <CollectionGestureContext.Provider value={'embedded'}>
        <TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>
      </CollectionGestureContext.Provider>
    );
    const grid = await getRoot();
    const items = await getItems('row');
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[1], { key: ' ' });
    fireEvent.keyDown(items[1], { key: ' ' });
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[2], { key: ' ' });
    expect(count).eq(4);
  });

  it('ItemAction - test press spacebar on item (Context value is default)', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    let count = 0;
    const onItemAction = <K, D>(detail: ItemActionDetail<K, D>) => {
      if (count === 1) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(detail.context.metadata, 'Context metadata is correct').eq(dataMetadata[1].metadata);
      } else if (count === 2) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[1].data);
        expect(detail.context.metadata, 'Context metadata is correct').eq(dataMetadata[1].metadata);
      } else if (count === 3) {
        expect(detail.context.data, 'Context data is correct').eq(dataMetadata[2].data);
        expect(detail.context.metadata, 'Context metadata is correct').eq(dataMetadata[2].metadata);
      }
      count++;
    };
    render(
      <CollectionGestureContext.Provider value={'default'}>
        <TestComponent data={dataMetadata} onItemAction={onItemAction}></TestComponent>
      </CollectionGestureContext.Provider>
    );
    const grid = await getRoot();
    const items = await getItems('row');
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(items[1], { key: ' ' });
    fireEvent.keyDown(items[1], { key: ' ' });
    expect(count).eq(1);
  });

  it('currentItemVariant - highlight', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: {
      data: Employee<string>;
      metadata: Metadata<string>;
    }[] = data.map((data) => {
      return { data: data, metadata: { key: data.id } };
    });
    render(
      <TestComponent
        data={dataMetadata}
        initialKey={dataMetadata[1].metadata.key}
        currentItemVariant="highlight"></TestComponent>
    );
    const items = await getItems();
    expect(isFocusHighlight(items[0])).to.be.false;

    fireEvent.click(items[0]);
    expect(isFocusHighlight(items[0])).to.be.true;
  });

  it('currentItemVariant - none', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: {
      data: Employee<string>;
      metadata: Metadata<string>;
    }[] = data.map((data) => {
      return { data: data, metadata: { key: data.id } };
    });
    render(
      <TestComponent
        data={dataMetadata}
        initialKey={dataMetadata[1].metadata.key}
        currentItemVariant="none"></TestComponent>
    );
    const items = await getItems();
    expect(isFocusHighlight(items[0])).to.be.false;

    fireEvent.click(items[0]);
    expect(isFocusHighlight(items[0])).to.be.false;
  });
};

export const testNavigation = (TestComponent: TestComponentProps<any, any>) => {
  it('arrow keys', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata}></TestComponent>);
    const grid = await getRoot();
    expect(grid.hasAttribute('aria-activedescendant')).eq(false);
    const items = await getContents();
    fireEvent.click(items[0]);
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[2].id);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[2].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[1].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
    fireEvent.keyDown(grid, { key: 'ArrowUp' });
    expect(grid.getAttribute('aria-activedescendant')).eq(items[0].id);
  });

  // test in karma mocha only since it involves scrolling
  if (typeof jest === 'undefined') {
    it('test scroll into view when listview regains focus', async () => {
      const data = populateData(10) as Employee<string>[];
      const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
        (data) => {
          return { data: data, metadata: { key: data.id } };
        }
      );

      // the initial current item is Employee 9
      render(<TestComponent data={dataMetadata} initialKey={data[9].id}></TestComponent>);

      const grid = await getRoot();
      await waitFor(() => {
        expect(grid.scrollTop).to.greaterThanOrEqual(280);
      });

      // scroll listview up until scrollTop is 0
      grid.scrollTop = 0;
      await waitFor(() => {
        expect(grid.scrollTop).to.eq(0);
      });

      // tab into listview, should scroll Employee 9 into view
      await userEvent.tab();
      await waitFor(() => {
        expect(grid.scrollTop).to.greaterThanOrEqual(280);
      });
    });
  }
};

export const testScrollPosition = (TestComponent: TestComponentProps<any, any>) => {
  if (typeof jest === 'undefined') {
    it('Scroll position', async () => {
      const data = populateData(20) as Employee<string>[];
      const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
        (data) => {
          return { data: data, metadata: { key: data.id } };
        }
      );

      render(
        <div style={`height: ${xUnits(50)}; overflow: auto`}>
          <TestComponent
            data={dataMetadata}
            scrollPositionOverride={{
              key: data[4].id
            }}></TestComponent>
        </div>
      );
      const position = screen.getByText(data[4].id + ' 200 0');
      expect(position).to.exist;
    });
  }
};

export const testReorder = (TestComponent: TestComponentProps<any, any>) => {
  it('reorder - fire Drag and Drop Event', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const { queryByTestId } = render(<TestComponent data={dataMetadata} isReorderEnabled={true} />);

    let items = await getItems();
    const count = items.length;
    const firstItemDragIcon = queryByTestId(
      `drag-icon-${items[0].dataset['ojKey']}`
    ) as HTMLElement;
    const thirdItemDragIcon = queryByTestId(
      `drag-icon-${items[2].dataset['ojKey']}`
    ) as HTMLElement;
    const firstItem = items[0];

    // drag the first item and drop on the third item
    fireDragAndDropEvent(firstItemDragIcon, thirdItemDragIcon);
    await waitFor(async () => {
      items = await getItems();
      const updatedThirdItem = items[2];
      expect(firstItem).equals(updatedThirdItem);
      expect(items.length).equal(count);
    });
  });

  it('reorder - reorder the first item to down using keyboard', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata} isReorderEnabled={true} />);

    const list = await getRoot();
    let items = await getItems();
    const count = items.length;
    const firstItem = items[0];

    // reorder the first item up
    mouseItemSelection(firstItem);
    keyboardReorder(list, 'ArrowUp');
    await waitFor(async () => {
      items = await getItems();
      const reorderedItem = items[0];
      expect(firstItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // reorder the first item down
    keyboardReorder(list, 'ArrowDown');
    await waitFor(async () => {
      items = await getItems();
      const reorderedItem = items[1];
      expect(firstItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // continuous reorder the first item down
    keyboardReorder(list, 'ArrowDown');
    await waitFor(async () => {
      items = await getItems();
      const reorderedItem = items[2];
      expect(firstItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });
  });

  it('reorder - reorder the last item to up using keyboard', async () => {
    const data = populateData(3) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    render(<TestComponent data={dataMetadata} isReorderEnabled={true} />);

    const list = await getRoot();
    let items = await getItems();
    const count = items.length;
    const startIndex = items.length - 1;
    const lastItem = items[startIndex];

    // reorder the last item down
    mouseItemSelection(lastItem.firstElementChild as HTMLElement);
    keyboardReorder(list, 'ArrowDown');
    await waitFor(async () => {
      items = await getItems();
      const reorderedItem = items[startIndex];
      expect(lastItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // reorder the last item up
    keyboardReorder(list, 'ArrowUp');
    await waitFor(async () => {
      items = await getItems();
      const reorderedItem = items[startIndex - 1];
      expect(lastItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // continuous reorder the last item up
    keyboardReorder(list, 'ArrowUp');
    await waitFor(async () => {
      items = await getItems();
      const reorderedItem = items[startIndex - 2];
      expect(lastItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });
  });

  if (typeof jest == typeof undefined) {
    it('reorder - test instruction text', async () => {
      const data = populateData(3) as Employee<string>[];
      const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
        (data) => {
          return { data: data, metadata: { key: data.id } };
        }
      );

      // get instruction text
      const { deviceType, platform } = getClientHints();
      const keyPressed =
        platform === 'windows' ? translations.accControlKey() : translations.accCommandKey();
      const reorderInstructions =
        deviceType === 'phone' || deviceType === 'tablet'
          ? ''
          : translations.accReorderInstructions({
              keyPressed: keyPressed
            });

      // instruction text should exist when reorder is enabled
      const { rerender } = render(<TestComponent data={dataMetadata} isReorderEnabled={true} />);
      let instructionText = screen.queryByText(reorderInstructions);
      expect(instructionText).not.to.be.null;

      // instruction text should not exist when reorder is disabled
      rerender(<TestComponent data={dataMetadata} />);
      instructionText = screen.queryByText(reorderInstructions);
      expect(instructionText).to.be.null;
    });
  }
};
